import { useQuery } from "@apollo/client";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useCallback, useMemo } from "react";

import { Table } from "../../../../../../components/Functional";
import { INVOICE_TYPE } from "../../../../../../constants/invoiceTypes";
import { UPDATE_ITEM as updateItemMutation } from "../../../../../../graphql/mutation/Item";
import { FETCH_ITEMS_V2 as fetchItemsV2 } from "../../../../../../graphql/query/Item";
import { FETCH_NOMINALS } from "../../../../../../graphql/query/Nominal";
import useUser from "../../../../../../hooks/useUser";
import SectionHeader from "../../components/SectionHeader";
import Actions from "./components/Actions";
import AddSalesPerson from "./components/AddSalesPerson";
import AddToSelectList from "./components/AddToSelectList";
import { AddTransactionModal } from "./components/AddTransaction";
import Information from "./components/Information";
import Total from "./components/Total";
import {
  formatHeaderAmount,
  isInvoiceApproved,
  isInvoicePaid,
  labelForInvoiceType,
} from "./headerSummary";

const isInputField = (props = {}, obj) => {
  const { formik = {} } = props;
  const { values } = formik;

  // if Credit note, then don't allow update
  if (values?.typeId === 6) {
    return { ...obj, type: "currency" };
  }

  // Disable the field if the transaction has been exported
  if (values?.exported !== 0) {
    return {
      ...obj,
      disabled: true,
      inputClassName: "!bg-white !text-gray-400 !border-gray-400",
    };
  }

  return obj;
};

// INV-606: once a transaction line has been added these fields are permanently
// read-only. Only Nominal and Advisor Commission / % stay editable until export
// (those use `disableConfig`, which locks them when the invoice is exported or
// the user lacks UPDATE_ITEM). Consignor Split / % stays editable even after
// export so Finance can amend it post-approval (gated by UPDATE_ITEM only).
// Discount / % / Amount / Tax are locked separately via `disabled: true` in
// their own configs.
const lockedFieldConfig = {
  disabled: true,
  inputClassName: "!bg-white !text-gray-400 !border-gray-400",
};

const InvoiceDetails = props => {
  const { invoice: fetchedInvoice, formik } = props;
  const { hasPermission } = useUser();
  const { data: { nominals = [] } = {} } = useQuery(FETCH_NOMINALS);
  const canReadInvoice = hasPermission("READ_ALL_INVOICE");

  if (!fetchedInvoice || !canReadInvoice) {
    window.location.href = "/invoice";
  }

  const { invoice, headerTitle } = useMemo(() => {
    const isExported = fetchedInvoice?.items?.some(item => item.exported !== 0);
    const { typeId, company = {}, number, proforma } = fetchedInvoice || {};
    const headerTitle = `${typeId === INVOICE_TYPE.INVOICE ? `${company.invoiceCode || ""}${number}` : `${proforma}`}`;

    return {
      invoice: { ...fetchedInvoice, isExported },
      headerTitle,
    };
  }, [fetchedInvoice]);

  const disableConfig = useCallback(
    exported => {
      const disabled =
        invoice?.isExported || exported === -1 || !hasPermission("UPDATE_ITEM");

      return {
        disabled,
        inputClassName: `!bg-white ${disabled ? "!text-gray-400 !border-gray-400" : ""}`,
      };
    },
    [invoice?.isExported, hasPermission],
  );

  // Consignor Split is deliberately excluded from the post-export lock so
  // authorised users can amend it after the sale has been exported and approved.
  const disableConsignorSplitConfig = useCallback(() => {
    const disabled = !hasPermission("UPDATE_ITEM");

    return {
      disabled,
      inputClassName: `!bg-white ${disabled ? "!text-gray-400 !border-gray-400" : ""}`,
    };
  }, [hasPermission]);

  if (!fetchedInvoice || !canReadInvoice) {
    return null;
  }

  const tableProps = {
    allowHorizontalScroll: true,
    variant: "dynamicHeight",
    dataKey: "itemsV2",
    headers: [
      {
        label: "Date",
        name: "date",
        type: "date-numeric",
        labelClass: props => {
          let labelColor = "";
          // Editable
          if (props?.row?.typeId !== 6) {
            labelColor = "text-sky-750";
          }
          // Immutable
          if (props?.row?.invoice?.immutable === -1) {
            labelColor = "text-pink";
          }
          // Exported
          if (props?.row?.exported !== 0) {
            labelColor = "text-grey-550";
          }
          return labelColor;
        },
      },
      { label: "Type", name: "type.name", type: "label" },
      props => ({
        label: "Nominal",
        name: "nominalId",
        inputType: "multi-select",
        options: nominals?.map(item => ({ label: item.label, value: item.id })),
        appendMissingOption: true,
        type: "input",
        ...disableConfig(props?.row?.exported),
      }),
      {
        label: "",
        name: "instance.edition.artwork.imagesSummary.imgT",
        type: "image",
      },
      props => {
        // Credit note lines keep the credited work's instance reference in
        // `ref`, but here we display the credit note's own number
        // (`creditNoteRef`) instead so the Reference column matches the
        // credit note document. Credit notes also drop the "open link"
        // affordance since the credit note number isn't an instance link.
        const isCreditNote = props?.row?.typeId === 6;

        return {
          inputType: isCreditNote ? "text" : "href",
          label: "Reference",
          name: isCreditNote ? "creditNoteRef" : "ref",
          rowClass: "w-[150px]",
          type: "input",
          ...(isCreditNote
            ? {}
            : {
                hrefLink:
                  props?.row?.instance?.id &&
                  `/instance/${props?.row?.instance?.id}/details`,
                disabledHref: !props?.row?.instance?.id,
              }),
          ...lockedFieldConfig,
        };
      },
      {
        inputType: "long-text",
        label: "Description",
        name: "description",
        type: "input",
        ...lockedFieldConfig,
      },
      props =>
        isInputField(props, {
          inputType: "enhanced-currency",
          label: "Discount",
          name: "discount",
          rowClass: "w-[150px]",
          type: "input",
          disabled: true,
        }),
      props =>
        isInputField(props, {
          inputType: "number",
          label: "%",
          name: "discountRate",
          rowClass: "w-[90px]",
          type: "input",
          disabled: true,
        }),
      {
        designType: "threeStateCheckbox",
        inputType: "toggle",
        label: "Hide",
        name: "showDiscount",
        rowClass: "w-[30px]",
        type: "input",
        ...lockedFieldConfig,
      },
      props =>
        isInputField(props, {
          inputType: "enhanced-currency",
          label: "Amount",
          name: "amount",
          rowClass: "w-[150px]",
          type: "input",
          disabled: true,
        }),
      props =>
        isInputField(props, {
          inputType: "enhanced-currency",
          label: "Tax",
          name: "taxAmount",
          rowClass: "w-[150px]",
          type: "input",
          allowNegativeInput: false,
          disabled: true,
        }),
      props => ({
        label: "Advisor Commission",
        name: "commission",
        inputType: "enhanced-currency",
        type: "input",
        allowNegativeInput: false,
        rowClass: "w-[150px]",
        ...disableConfig(props?.row?.exported),
      }),
      props => ({
        inputType: "number-currency",
        label: "%",
        name: "commissionRate",
        rowClass: "w-[90px]",
        type: "input",
        allowNegativeInput: false,
        ...disableConfig(props?.row?.exported),
      }),
      () => ({
        inputType: "enhanced-currency",
        label: "Consignor Split",
        name: "consigneeSplit",
        type: "input",
        allowNegativeInput: false,
        ...disableConsignorSplitConfig(),
      }),
      () => ({
        inputType: "number-currency",
        label: "%",
        name: "consigneeSplitRate",
        rowClass: "w-[90px]",
        type: "input",
        allowNegativeInput: false,
        ...disableConsignorSplitConfig(),
      }),
      {
        label: "",
        name: "action",
        width: "32px",
        rowClass: "w-[32px]",
        type: "custom",
        component: item => (
          <Actions
            item={item}
            invoiceId={invoice?.id}
            invoiceItems={invoice?.items}
          />
        ),
      },
    ],
    inputs: [],
    FETCH_QUERY: fetchItemsV2({
      instance: { edition: { artwork: { imagesSummary: { imgT: {} } } } },
      invoice: {},
      type: {},
    }),
    UPDATE_MUTATION: updateItemMutation(),
    REFETCH_QUERIES: ["FetchInvoice"],
    variables: {
      invoiceId: {
        operator: "eq",
        value: invoice?.id,
      },
      sort1: { value: "date", operator: "ASC" },
    },
  };

  const invoiceActions = [
    {
      component: () => {
        return (
          <>
            <AddToSelectList invoiceId={invoice?.id} />
            <AddTransactionModal invoice={invoice} />
            <AddSalesPerson invoice={invoice} />
          </>
        );
      },
    },
  ];

  const typeLabel = labelForInvoiceType(invoice?.typeId);
  const approved = isInvoiceApproved(invoice);
  const paid = isInvoicePaid(invoice);
  const exported = Boolean(invoice?.isExported);
  const currencyCode = invoice?.currency?.code || "";

  return (
    <>
      <SectionHeader
        title={
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <span>{`${typeLabel} ${headerTitle}`}</span>
              {approved && (
                <span className="inline-flex items-center gap-1 rounded-full border border-yellow-800 bg-yellow-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-yellow-800">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" />
                  Approved
                </span>
              )}
              {exported && (
                <span className="inline-flex items-center gap-1 rounded-full border border-green-800 bg-green-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-green-800">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" />
                  Exported
                </span>
              )}
              {paid && (
                <span className="inline-flex items-center gap-1 rounded-full border border-red-800 bg-rose-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-red-800">
                  <CheckIcon className="h-3 w-3" aria-hidden="true" />
                  Paid
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-base font-normal">
              <div>
                <span className="text-gray-500">Total: </span>
                {formatHeaderAmount(
                  invoice?.invoiceTotals?.total,
                  currencyCode,
                )}
              </div>
              <div>
                <span className="text-gray-500">Balance: </span>
                {formatHeaderAmount(
                  invoice?.invoiceTotals?.balance,
                  currencyCode,
                )}
              </div>
            </div>
          </div>
        }
        actions={invoiceActions}
      />
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <Information formik={formik} invoice={invoice} submitOnBlur={true} />
        <Total formik={formik} invoice={invoice} />
      </div>
      <div className="w-full">
        <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <h5 className="font-bold">Transactions</h5>
          <AddTransactionModal invoice={invoice} />
        </div>
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default InvoiceDetails;
